import { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { permissionOptions } from '../../../utils/Enums';
import PluginsContext from '../../../context/PluginsContext';

const EMPTY_FORM = {
  name: '',
  description: '',
  responsible_staff_email: ''
};

export default function BusinessUnitsSection({
  businessUnits,
  locations,
  staff,
  isSuperAdmin = false,
  onCreate,
  onUpdate,
  onDelete
}) {
  const { t } = useTranslation();
  const { hasPlugin } = useContext(PluginsContext);
  const b2bPluginMissing = !hasPlugin('b2b');
  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionLabel) => {
    const permission = permissionOptions.find((option) => option.label === permissionLabel);
    return permission ? userPermissions.includes(permission.id) : false;
  };
  const canViewBusinessUnits = hasPermission('VIEW_BUSINESS_UNITS');
  const canCreateBusinessUnits = isSuperAdmin;
  const canUpdateBusinessUnits = hasPermission('UPDATE_BUSINESS_UNITS');
  const canDeleteBusinessUnits = hasPermission('DELETE_BUSINESS_UNITS');
  const [open, setOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [unitToDelete, setUnitToDelete] = useState(null);

  const mainLocations = useMemo(
    () => locations.filter((location) => !location.businessUnitId),
    [locations]
  );

  const openCreate = () => {
    setEditingUnit(null);
    setFormData(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      name: unit.name || '',
      description: unit.description || '',
      responsible_staff_email: unit.responsibleStaffEmail || ''
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      return;
    }
    if (editingUnit) {
      await onUpdate(editingUnit.id, formData);
    } else {
      await onCreate(formData);
    }
    setOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!unitToDelete) {
      return;
    }
    await onDelete(unitToDelete.id);
    setUnitToDelete(null);
  };

  if (!canViewBusinessUnits) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <div>
          <Typography variant="h6">Estructura</Typography>
          <Typography variant="body2" color="text.secondary">
            Main + franquicias + sucursales
          </Typography>
        </div>
        {canCreateBusinessUnits && (
          <Button
            variant="outlined"
            startIcon={getIcon('add')}
            onClick={openCreate}
          >
            Nueva franquicia
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Main</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sucursales sin franquicia asignada
              </Typography>
              {mainLocations.map((location) => (
                <Box key={location.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{location.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {location.isPlant ? 'Planta' : 'Sucursal'}
                  </Typography>
                </Box>
              ))}
              {mainLocations.length === 0 && (
                <Typography variant="body2" color="text.secondary">Sin sucursales main.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {businessUnits.map((unit) => {
          const unitLocations = locations.filter((location) => location.businessUnitId === unit.id);
          return (
            <Grid item xs={12} md={6} key={unit.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <div>
                      <Typography variant="h6">{unit.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {unit.description || 'Sin descripcion'}
                      </Typography>
                    </div>
                    <Box>
                      <IconButton size="small" onClick={() => openEdit(unit)} disabled={!canUpdateBusinessUnits}>
                        {getIcon('settings')}
                      </IconButton>
                      <IconButton size="small" onClick={() => setUnitToDelete(unit)} disabled={!canDeleteBusinessUnits}>
                        {getIcon('trash')}
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Responsable: {unit.responsibleStaffName || unit.responsibleStaffEmail || 'Sin asignar'}
                  </Typography>
                  {unitLocations.map((location) => (
                    <Box key={location.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">{location.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {location.isPlant ? 'Planta' : 'Sucursal'}
                      </Typography>
                    </Box>
                  ))}
                  {unitLocations.length === 0 && (
                    <Typography variant="body2" color="text.secondary">Sin sucursales asignadas.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUnit ? 'Editar franquicia' : 'Nueva franquicia'}</DialogTitle>
        <DialogContent>
          {b2bPluginMissing && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Para que esta franquicia pueda reponer stock y calcular sus costos correctamente, necesitás activar el plugin B2B (Configuración &gt; Plugins).
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t('name')}
            fullWidth
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
          />
          <TextField
            margin="dense"
            label={t('description')}
            fullWidth
            value={formData.description}
            onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
          />
          <Select
            fullWidth
            displayEmpty
            value={formData.responsible_staff_email}
            onChange={(event) => setFormData((prev) => ({ ...prev, responsible_staff_email: event.target.value }))}
            sx={{ mt: 2 }}
          >
            <MenuItem value="">Sin responsable</MenuItem>
            {staff.map((user) => (
              <MenuItem key={user.email} value={user.email}>
                {user.firstName} {user.lastName}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleSave}>{t('save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!unitToDelete} onClose={() => setUnitToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar franquicia</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`Vas a eliminar la franquicia "${unitToDelete?.name || ''}". Esta accion no se puede deshacer.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitToDelete(null)}>{t('cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
